import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, UploadCloud, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

export const ShareCatch = ({ label = "Share Your Catch", variant = "solid", testid = "share-catch" }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [quote, setQuote] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setName(""); setLocation(""); setQuote(""); setFile(null); setPreview(""); setDone(false);
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Add a catch photo to share");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("location", location);
      fd.append("quote", quote);
      fd.append("file", file);
      await api.post("/testimonials/submit", fd);
      reset();
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 300);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`group inline-flex items-center gap-3 font-mono-plex text-xs tracking-[0.2em] uppercase px-7 py-4 transition-all active:scale-95 ${
          variant === "solid"
            ? "bg-orange-500 text-[#04070A] hover:bg-orange-600"
            : "border border-white/20 text-slate-200 hover:bg-white/5"
        }`}
        data-testid={`${testid}-button`}
      >
        <Camera className="w-4 h-4" />
        {label}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={close}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A1118] border border-white/10 w-full max-w-lg my-8"
              data-testid={`${testid}-modal`}
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
                <h2 className="font-display text-2xl uppercase tracking-tight">Share Your Catch</h2>
                <button onClick={close} data-testid={`${testid}-close`}>
                  <X className="w-5 h-5 text-slate-400 hover:text-orange-500" />
                </button>
              </div>

              {done ? (
                <div className="p-10 text-center" data-testid={`${testid}-success`}>
                  <CheckCircle2 className="w-14 h-14 text-orange-500 mx-auto" />
                  <h3 className="font-display text-2xl uppercase tracking-tight mt-6">Thanks!</h3>
                  <p className="text-slate-400 text-sm mt-3">
                    Your photo is in the queue. Once we give it a quick look, it&rsquo;ll show up in the gallery.
                  </p>
                  <button
                    onClick={close}
                    className="mt-8 border border-white/20 font-mono-plex text-xs tracking-[0.2em] uppercase px-8 py-3 hover:bg-white/5 transition-colors"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="p-6 space-y-5">
                  {preview ? (
                    <div className="relative w-full aspect-[4/3] border border-white/10 overflow-hidden">
                      <img src={preview} alt="Your catch" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => { setFile(null); setPreview(""); }}
                        className="absolute top-2 right-2 bg-black/70 p-1.5"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center gap-2 w-full aspect-[4/3] border border-dashed border-white/25 cursor-pointer hover:border-orange-500 transition-colors" data-testid={`${testid}-upload-label`}>
                      <UploadCloud className="w-8 h-8 text-slate-400" />
                      <span className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">Add your catch photo</span>
                      <input type="file" accept="image/*" onChange={pickFile} className="hidden" data-testid={`${testid}-upload-input`} />
                    </label>
                  )}
                  <div>
                    <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Your Name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none text-sm" data-testid={`${testid}-name`} />
                  </div>
                  <div>
                    <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Location / Water</label>
                    <input value={location} onChange={(e) => setLocation(e.target.value)} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none text-sm" data-testid={`${testid}-location`} placeholder="e.g. Lake Fork, TX" />
                  </div>
                  <div>
                    <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Caption (optional)</label>
                    <textarea rows={2} value={quote} onChange={(e) => setQuote(e.target.value)} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none text-sm resize-none" data-testid={`${testid}-quote`} placeholder="Which bait? How'd it go?" />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-[0.2em] uppercase py-4 hover:bg-orange-600 transition-colors disabled:opacity-60"
                    data-testid={`${testid}-submit`}
                  >
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Submit Photo"}
                  </button>
                  <p className="text-slate-600 text-[11px] text-center">Submissions are reviewed before appearing in the gallery.</p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
